/* eslint-disable max-lines */
import React, {PureComponent, Fragment, ReactNode} from 'react';

import {CtaButton, Modal, ShadowCard} from '@optimaxdev/design-system';
import classNames from 'classnames';
import {connect} from 'react-redux';
import {Dispatch} from 'redux';

import {CloudinaryImage} from 'components/cloudinaryImage/cloudinaryImage';
import {QuestionMark} from 'desktop/components/helpers/icons/questionMark/questionMark';
import {ApplicationStoreType} from 'desktop/store';
import {ActionCreatorType} from 'types/reducers';

import {setActiveUpsell} from '../../store/reducers/activeUpsell/activeUpsell';
import {openPopupPayment} from '../../store/reducers/orderLast/orderLast';
import {UpsellType} from '../../types/upsellsTypes';
import {EnhancedCoverageOneYear} from '../enhancedCoverageOneYear/enhancedCoverageOneYear';
import {UpgradeMatchPrice} from '../upgradeMatchPrice/upgradeMatchPrice';

import s from './upgradeItem.scss';

type PropsType = {
    openPaymentPopup: ActionCreatorType<boolean>;
    setUpsell: ActionCreatorType<UpsellType>;
    upsell: UpsellType;
    phone: string;
    disabled?: boolean;
};
type StateType = {
    isPopupVisible?: Node | null | boolean;
};

/**
 * Upgrade item component
 */
export class UpgradeItem extends PureComponent<PropsType, StateType> {
    static defaultProps = {
        disabled: false,
    };
    state = {
        isPopupVisible: false,
    };

    /**
     * Trying to find assets for upsell by type or sku and return object with them,
     * return empty object if nothing was found
     * @param {UpsellType} upsell - Thank you page upsell
     * @returns {{image: ?string, popup: ?Node}} Image name
     */
    getUpsellData = (
        upsell: UpsellType,
    ): {
        image: string;
        popup?: ReactNode | null;
    } => {
        const {phone} = this.props;
        const {copayPrice, price, sku, type, upsellSku} = upsell;
        const namePackage = upsellSku ? upsellSku.split('-')[0] : '';
        const isMultifocal = namePackage.toLowerCase() === 'multifocal';
        const data = {
            change_package: {
                image: isMultifocal
                    ? require('./images/multifocal.jpg')
                    : require('./images/lenses.jpg'),
            },
            digital_protection: {
                image: require('./images/digital.jpg'),
            },
            hydrophobic: {
                image: require('./images/hydrophobic.jpg'),
            },
            insurance: {
                image: require('./images/enhanced.jpg'),
                popup: (
                    <EnhancedCoverageOneYear
                        copayPrice={
                            typeof copayPrice === 'number'
                                ? copayPrice.toString()
                                : copayPrice ?? ''
                        }
                        handleClick={null}
                        isSelected={false}
                        phone={phone}
                        price={price}
                    />
                ),
            },
            light_adjusting: {
                image: require('./images/photochromic.jpg'),
            },
            // Eugenii Gordeev said that we don't use the transition upsell on TYP anymore
            transition: {
                image: require('./images/adaptive.gif'),
            },
        };

        return (
            data[type as keyof typeof data] ||
            data[sku as keyof typeof data] ||
            ({} as {
                image: string;
            })
        );
    };

    /**
     * Open upsell popup
     */
    openPopup = () => {
        const {openPaymentPopup, setUpsell, upsell} = this.props;
        openPaymentPopup(true);
        if (upsell) {
            setUpsell(upsell);
        }
    };

    /**
     * Toggle upsell popup
     */
    toggleUpsellPopup = () => {
        this.setState(({isPopupVisible}) => ({
            isPopupVisible: !isPopupVisible,
        }));
    };

    /**
     * Render component
     * @return {Node} component
     */
    render() {
        const {disabled, upsell} = this.props;
        const {isPopupVisible} = this.state;
        const buttonText = upsell.type === 'insurance' ? 'Add to Order' : 'Upgrade Lenses';
        const {image, popup} = this.getUpsellData(upsell);
        return (
            <Fragment>
                <ShadowCard className={s.cardContainer} corner={4} saturate={2}>
                    <div
                        className={classNames(s.wrapper, {
                            [s.disabled]: disabled,
                        })}
                    >
                        <div className={s.content}>
                            <p className={s.title}>
                                {upsell.description}
                                {popup && (
                                    <button
                                        className={s.upsellPopupBtn}
                                        disabled={disabled}
                                        onClick={this.toggleUpsellPopup}
                                        type="button"
                                    >
                                        <QuestionMark />
                                    </button>
                                )}
                            </p>
                            <UpgradeMatchPrice
                                match={upsell.match}
                                oldPrice={upsell.oldPrice}
                                price={upsell.price}
                            />
                            <div className={s.button}>
                                <CtaButton
                                    isDisabled={disabled}
                                    onClick={this.openPopup}
                                    size="extraSmall"
                                    isFull
                                >
                                    {buttonText}
                                </CtaButton>
                            </div>
                        </div>
                        <div className={s.upsellImage}>
                            <CloudinaryImage
                                alt="Upsell"
                                className={s.image}
                                height="107"
                                src={image}
                                width="107"
                            />
                        </div>
                    </div>
                </ShadowCard>
                {popup && (
                    <Modal
                        contentClassName={s.modal}
                        isOpen={isPopupVisible}
                        onRequestClose={this.toggleUpsellPopup}
                        overlay="dark"
                    >
                        {popup}
                    </Modal>
                )}
            </Fragment>
        );
    }
}

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (state: ApplicationStoreType) => ({
    disabled: state.successPage.upsells.isUpsellsDisabled,
});

/**
 * Map dispatch to props
 * @returns {PropsType} props
 */
const mapDispatchToProps = (dispatch: Dispatch) => ({
    openPaymentPopup: (status: boolean) => dispatch(openPopupPayment(status)),
    setUpsell: (upsell: UpsellType) => dispatch(setActiveUpsell(upsell)),
});

export const UpgradeItemConnected = connect(mapStateToProps, mapDispatchToProps)(UpgradeItem);
